import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type {
  SearchAssistantConversationsResponse,
  SearchConversationsParams,
} from '@borradh-workspace/api-client/types';
import { searchAssistantConversationsResponseSchema } from '@borradh-workspace/contracts';
import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';

/**
 * `GET /assistant/conversations/search` — the sidebar "Chat history" search
 * (ENG-1161). Matches conversation titles AND message text, server-side.
 *
 * Keyed under `['assistant', 'conversations', …]` so the rename / delete /
 * create mutations, which invalidate `['assistant', 'conversations']`, also
 * refresh any open search.
 */
export const searchConversationsQueryOptions = (
  params: SearchConversationsParams
) => {
  const q = params.q.trim();
  // `limit` arrives as the zod input type (unknown) — narrow it for the key.
  const limit = typeof params.limit === 'number' ? params.limit : undefined;
  return queryOptions({
    queryKey: queryKeys.assistant.conversationSearch(q, limit),
    queryFn: () =>
      apiClient.get<SearchAssistantConversationsResponse>(
        'assistant/conversations/search',
        {
          searchParams: {
            q,
            ...(params.limit ? { limit: String(params.limit) } : {}),
          },
          schema: searchAssistantConversationsResponseSchema,
        }
      ),
    // A blank term is "not searching", not "search for everything" — the
    // caller shows the plain history list instead, and the API would 400.
    enabled: q.length > 0,
    staleTime: 30 * 1000,
  });
};

export const useSearchConversations = (params: SearchConversationsParams) => {
  const query = useQuery({
    ...searchConversationsQueryOptions(params),
    // Keep the last results on screen while the next keystroke's request is in
    // flight, instead of flashing the loading state on every character.
    placeholderData: keepPreviousData,
  });
  return {
    results: query.data?.conversations ?? [],
    isSearching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};
